// Layer 3 (Transformation): the bearing case page's handoff region (W3d, D16;
// equipment-beat comp Handoff A). Three reads, one per column:
//   1. the condition evidence: TE-622-625 over the week to the latest sample,
//      the Band high crossing, the post-restart rate and the spec-high horizon;
//   2. the one-off stop pair: planned stop vs avoided failure, priced by the
//      fixture's `one_off_stop_pair` (hours × the capacity EBITDA rate);
//   3. the maintenance handoff: the checks before release, the draft work
//      order's fields and who decides the stop (reconciliation A6).
// Pure: no React, no engine mutation. Figures come from the fixture, the seed
// series and `bm01BearingTrend`; nothing here types an amount, a rate or a date.

import type { AnalysisResult } from '@/shared/contracts/analytics/v1';
import type { Case, FailureMode } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { addMinutes, diffMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { formatCurrencyExact, CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import { sampleGaps } from '@/versions/v1/shared/series-gaps.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { BM01_BEARING_SERIES_SHAPE } from '@/versions/v1/shared/bunker-hill-bearing-trend';
import { DOWNTIME_STATES } from '../maintenance/downtime.seed';
import { bm01BearingTrend } from '../downtime/ValueLost.transform';
import { plantRoleLabel } from './AgentInvestigation.transform';
import type { stopActionRead } from './StopAction.transform';

/** The week the comp draws: seven days back from the latest sample. */
const TREND_WINDOW_DAYS = 7;
const BEARING_SERIES_ID = 'rs-bm-brg-temp';

type StopActionRead = NonNullable<ReturnType<typeof stopActionRead>>;

export type HandoffTrendPoint = { at: SeriesTime; value: number | null };

export type HandoffTrendChart = {
  points: readonly HandoffTrendPoint[];
  window: { startAt: SeriesTime; endAt: SeriesTime };
  unit: string;
  bandHigh: number;
  specHigh: number;
  /** First sample above Band high after the restart; the tint runs from here. */
  crossingAt: SeriesTime | null;
  /** The measured samples from the crossing on, which the chart tints above Band high. */
  aboveBand: readonly { at: SeriesTime; value: number }[];
  /** Runs of missing samples (the WO-441 trip), bounded by the samples either side. */
  gaps: readonly { startAt: SeriesTime; endAt: SeriesTime }[];
  /** The cursor sample; null before the series has any observation (#782). */
  latest: { at: SeriesTime; value: number } | null;
  yDomain: { min: number; max: number };
  yTicks: readonly number[];
  dayTicks: readonly { at: SeriesTime; label: string }[];
};

export type HandoffFact = { id: string; label: string; value: string; tone?: 'blocked' };

export type HandoffConditionRead = {
  title: string;
  period: string;
  chart: HandoffTrendChart;
  facts: readonly HandoffFact[];
};

export type HandoffStopBar = {
  id: 'planned' | 'avoided';
  label: string;
  detail: string;
  amount: string;
  /** Bar length as a share of the longer stop, 0–100. */
  widthPct: number;
};

export type HandoffStopRead = {
  bars: readonly HandoffStopBar[];
  /** The `hours × <rate>` leading term; absent for a floor seat, which reads no US$. */
  basis: { rate?: string; text: string };
  rows: readonly { label: string; value: string }[];
};

export type HandoffGateRead = {
  owner: string;
  checks: readonly { at: string; lead: string; detail: string; basis?: string }[];
  /** Reconciliation A4: how long the response takes and when the case may close. */
  recoveryLine: string;
  lead: string;
  approvedLead: string;
  fields: readonly { label: string; value: string; muted?: string }[];
  tarpLine: string | null;
  stopDecision: { decides: string; confirms: string; escalation: string } | null;
};

function oneDecimal(value: number): string {
  return value.toFixed(1);
}

function signedOneDecimal(value: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}`;
}

function dayClock(at: SeriesTime): string {
  return `${formatDayMonth(at)} ${at.slice(11, 16)}`;
}

function capitalise(value: string): string {
  return value.length === 0 ? value : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

/** `01–08 Jun` for one month, `28 May–04 Jun` across two. */
function periodLabel(startAt: SeriesTime, endAt: SeriesTime): string {
  const start = formatDayMonth(startAt);
  const end = formatDayMonth(endAt);
  return start.slice(3) === end.slice(3) ? `${start.slice(0, 2)}–${end}` : `${start}–${end}`;
}

/** Midnight ticks every second day, counted back from the latest day so the last tick is today. */
function dayTicks(window: { startAt: SeriesTime; endAt: SeriesTime }): HandoffTrendChart['dayTicks'] {
  const lastMidnight = `${window.endAt.slice(0, 10)}T00:00` as SeriesTime;
  const ticks: { at: SeriesTime; label: string }[] = [];
  for (let day = 0; ; day += 2) {
    const at = addMinutes(lastMidnight, -day * 24 * 60);
    if (at < window.startAt) break;
    ticks.unshift({ at, label: formatDayMonth(at) });
  }
  return ticks;
}

/**
 * Column 1: the bearing's week on the seed series (the same `rs-bm-brg-temp`
 * the Maintenance page and the fixture's observations carry), against the
 * case's Control Plan row. `bandCrossingAt` is the case read's frame fact, so
 * the tint and the fact name the same instant.
 */
export function handoffConditionRead(input: {
  state: SessionState;
  signalTag: string;
  controlPlan: { bandHigh: number; specHigh: number; unit: string };
  bandCrossingAt?: SeriesTime;
}): HandoffConditionRead {
  const { state, signalTag, controlPlan, bandCrossingAt } = input;
  const trend = bm01BearingTrend(state);
  const fit = trend.state === 'fitted' ? trend : null;
  const series = seriesAt(BEARING_SERIES_ID, state);
  // The window ends on the cursor's measured sample; before the series has one
  // (or before the restart can be fitted, #782) it ends on the cursor itself.
  const endAt = trend.currentAt ?? state.cursor;
  const startAt = addMinutes(endAt, -TREND_WINDOW_DAYS * 24 * 60);
  const points = series.points
    .filter((point) => point.at >= startAt && point.at <= endAt)
    .map((point) => ({ at: point.at, value: point.value }));
  const values = points.flatMap((point) => (point.value === null ? [] : [point.value]));
  const min = Math.min(...values, controlPlan.bandHigh);
  const max = Math.max(...values, controlPlan.specHigh);
  const yDomain = { min: Math.floor(min) - 6, max: Math.ceil(max) + 2 };
  const yTicks: number[] = [];
  for (let tick = Math.ceil(yDomain.min / 10) * 10; tick < yDomain.max; tick += 10) yTicks.push(tick);
  const crossingAt = bandCrossingAt && bandCrossingAt <= endAt ? bandCrossingAt : null;
  const aboveBand = crossingAt
    ? points.flatMap((point) => (point.at >= crossingAt && point.value !== null ? [{ at: point.at, value: point.value }] : []))
    : [];
  const unit = controlPlan.unit;

  const facts: HandoffFact[] = [
    {
      id: 'Latest reading',
      label: `Latest · ${endAt.slice(11, 16)}`,
      value: trend.currentValue === null ? 'unmeasured' : `${oneDecimal(trend.currentValue)} ${unit}`,
      tone: trend.currentValue !== null && trend.currentValue > controlPlan.bandHigh ? 'blocked' : undefined,
    },
  ];
  if (crossingAt) {
    facts.push({ id: 'Band crossing', label: `Band high ${controlPlan.bandHigh} ${unit} crossed`, value: dayClock(crossingAt) });
  }
  if (fit) {
    facts.push({
      id: 'Post-restart rate',
      label: `Since ${formatDayMonth(fit.restartAt)} restart`,
      value: `${signedOneDecimal(fit.slopeCPerDay)} ${unit}/day`,
    });
  } else {
    // Before the restart the rate is unmeasured, never a window slope across the trip (#782).
    facts.push({ id: 'Post-restart rate', label: 'Post-restart rate', value: 'unmeasured' });
  }
  if (fit && fit.daysToLimit !== null && fit.limitDate !== null) {
    facts.push({
      id: 'Spec high horizon',
      label: `Spec high ${controlPlan.specHigh} ${unit} reached`,
      value: `≈ ${fit.daysToLimit.toFixed(1)} d · ${fit.limitDate}`,
    });
  }

  return {
    title: `${signalTag} bearing temperature`,
    period: periodLabel(startAt, endAt),
    chart: {
      points,
      window: { startAt, endAt },
      unit,
      bandHigh: controlPlan.bandHigh,
      specHigh: controlPlan.specHigh,
      crossingAt,
      aboveBand,
      gaps: sampleGaps(points),
      latest: trend.currentValue === null ? null : { at: endAt, value: trend.currentValue },
      yDomain,
      yTicks,
      dayTicks: dayTicks({ startAt, endAt }),
    },
    facts,
  };
}

/**
 * Column 2: the one-off pair, stated once on the page. Amounts, hours and the
 * rate are the fixture's; the projection and the prior stop come from the same
 * post-restart trend and the WO-441 downtime interval.
 */
export function handoffStopRead(stop: StopActionRead, state: SessionState, showMoney = true): HandoffStopRead {
  const { pair } = stop;
  const trend = bm01BearingTrend(state);
  const trip = DOWNTIME_STATES.find((row) => row.workOrderId === BM01_BEARING_SERIES_SHAPE.trip.workOrderId);
  const longer = Math.max(pair.planned_hours, pair.avoided_failure_hours);
  const rows: { label: string; value: string }[] = [];
  if (trend.state === 'fitted' && trend.limitAt) rows.push({ label: `${trend.limitC} °C projected`, value: dayClock(trend.limitAt) });
  if (trip) rows.push({ label: 'Prior stop', value: `${formatDayMonth(trip.start)} trip · ${BM01_BEARING_SERIES_SHAPE.trip.workOrderId}` });
  return {
    bars: [
      {
        id: 'planned',
        label: 'Planned inspection',
        // #731: the floor seats read the stop in hours, never its priced amount.
        detail: showMoney ? `${pair.planned_hours} h · ${stop.windowDay}` : stop.windowDay,
        amount: showMoney ? formatCurrencyExact(pair.planned_amount) : `${pair.planned_hours} h`,
        widthPct: (pair.planned_hours / longer) * 100,
      },
      {
        id: 'avoided',
        label: 'Avoided failure',
        detail: showMoney ? `${pair.avoided_failure_hours} h repair` : 'repair',
        amount: showMoney ? formatCurrencyExact(pair.avoided_failure_amount) : `${pair.avoided_failure_hours} h`,
        widthPct: (pair.avoided_failure_hours / longer) * 100,
      },
    ],
    basis: {
      rate: showMoney ? formatCurrencyExact(pair.rate_per_operating_hour) : undefined,
      text: showMoney
        ? `EBITDA per operating hour · one-off, outside the ${CURRENCY_GLYPH}/day exposure`
        : 'one-off · outside the daily exposure',
    },
    rows,
  };
}

/**
 * Column 3: what Maintenance reads before approving, and what the approval
 * hands on. The work order, checks and stop decision are the case's stop plan;
 * the TARP tier and owner are the fixture's top candidate; the handler and
 * respond-by clock are the case seed.
 */
export function handoffGateRead(input: {
  caseItem: Case;
  stop: StopActionRead;
  result?: AnalysisResult;
  failureMode?: FailureMode | null;
}): HandoffGateRead {
  const { caseItem, stop, result, failureMode } = input;
  const plan = caseItem.stopPlan;
  const tarp = result?.diagnosis?.candidates[0]?.tarp;
  const handler = caseItem.handler?.name;
  const fields: { label: string; value: string; muted?: string }[] = [];
  if (plan?.workOrder) fields.push({ label: 'Asset', value: plan.workOrder.asset, muted: plan.workOrder.criticality });
  if (failureMode) fields.push({ label: 'FMEA mode', value: failureMode.failureMode });
  fields.push({ label: 'Owner', value: `${handler ? `${handler} · ${stop.owner}` : stop.owner} · ${stop.windowShift} shift` });
  if (plan?.workOrder) fields.push({ label: 'Kit', value: plan.workOrder.kit });
  const escalatesTo = caseItem.respondBy?.escalatesTo ?? tarp?.escalate_to_role;
  const escalateLabel = escalatesTo ? plantRoleLabel(escalatesTo) ?? capitalise(escalatesTo) : undefined;
  const tarpLine = tarp
    ? [`TARP tier ${tarp.tier}`, caseItem.respondBy ? `respond by ${caseItem.respondBy.at}${escalateLabel ? `, then ${escalateLabel}` : ''}` : undefined]
        .filter(Boolean).join(' · ')
    : null;
  return {
    owner: handler ?? stop.owner,
    checks: (plan?.releaseChecks ?? []).map((check) => ({
      at: check.onStopDay ? (plan?.window.at ? formatDayMonth(plan.window.at as SeriesTime) : plan?.window.label ?? '') : check.at,
      lead: check.lead,
      detail: check.detail,
      basis: check.basis,
    })),
    recoveryLine: stop.recoveryLine,
    // One line at 1470: the shift rides on the owner row.
    lead: `Work order${plan ? ` ${plan.workOrderId}` : ''} · ${stop.pair.planned_hours} h inspection, ${stop.windowDay}`,
    approvedLead: stop.approvedProposition,
    fields,
    tarpLine,
    stopDecision: plan?.stopDecision
      ? { decides: plan.stopDecision.decides, confirms: plan.stopDecision.confirms, escalation: `Escalated to ${plan.stopDecision.decides} · due ${plan.stopDecision.due}` }
      : null,
  };
}

/** Horizontal position of a sample in the trend window, 0–1. */
export function handoffTrendX(at: SeriesTime, window: HandoffTrendChart['window']): number {
  const span = diffMinutes(window.endAt, window.startAt);
  return span <= 0 ? 0 : diffMinutes(at, window.startAt) / span;
}
